<template>
  <div class="stock-pkg-cell" :class="{ 'stock-pkg-cell--with-cost': showsCost }">
    <div
      v-for="row in rows"
      :key="row.key"
      class="stock-pkg-cell__row"
    >
      <v-tooltip bottom :disabled="!row.hint">
        <template v-slot:activator="{ on, attrs }">
          <div class="stock-pkg-cell__unit" v-bind="attrs" v-on="on">
            <span class="stock-pkg-cell__name">{{ row.name }}</span>
            <span v-if="row.weightLabel" class="stock-pkg-cell__kg">{{ row.weightLabel }}</span>
          </div>
        </template>
        <span>{{ row.hint }}</span>
      </v-tooltip>
      <div class="stock-pkg-cell__qty">{{ row.quantityLabel }}</div>
      <div
        v-if="showsCost"
        class="stock-pkg-cell__cost"
        :class="{ 'stock-pkg-cell__cost--empty': !row.unitCostLabel }"
      >
        {{ row.unitCostLabel || "—" }}
      </div>
    </div>
    <span v-if="!rows.length" class="grey--text">—</span>
  </div>
</template>

<script>
import { breakdownDisplayRows, formatQty } from "@/common/stock/productPackagingConversion";

export default {
  name: "StockPackagingBreakdownCell",
  props: {
    breakdown: {
      type: Object,
      default: null,
    },
    showCost: {
      type: Boolean,
      default: false,
    },
    averageCost: {
      default: null,
    },
    currencyCode: {
      type: String,
      default: "",
    },
  },
  computed: {
    showsCost() {
      return this.showCost;
    },
    hasAverageCost() {
      return this.averageCost != null && this.averageCost !== "" && Number.isFinite(Number(this.averageCost));
    },
    rows() {
      return breakdownDisplayRows(this.breakdown).map((row) => {
        const weightKg = Number(row.weightKg);
        const hasWeight = Number.isFinite(weightKg) && weightKg > 0;
        const unitCost = this.hasAverageCost && hasWeight
          ? Number(this.averageCost) * weightKg
          : null;

        return {
          ...row,
          quantityLabel: formatQty(row.quantity),
          weightLabel: hasWeight ? `${formatQty(weightKg)} kg` : null,
          unitCostLabel: this.formatMoney(unitCost),
          hint: this.buildHint(row.name, hasWeight ? weightKg : null, unitCost),
        };
      });
    },
  },
  methods: {
    formatMoney(value) {
      if (value == null || !Number.isFinite(Number(value))) {
        return null;
      }

      const formatted = Number(value).toFixed(4).replace(/\.?0+$/, "");
      return `${formatted === "-0" ? "0" : formatted} ${this.currencyCode || ""}`.trim();
    },
    buildHint(unitName, weightKg, unitCost) {
      if (this.showCost && unitCost != null && weightKg != null) {
        return this.$t("STOCK.AVERAGE_COSTS.PACKAGING_COST_HINT", {
          unit: unitName,
          kg: formatQty(weightKg),
        });
      }

      if (weightKg != null) {
        return this.$t("STOCK.PRODUCTS.PACKAGING_UNIT_HINT", {
          unit: unitName,
          kg: formatQty(weightKg),
        });
      }

      return "";
    },
  },
};
</script>

<style scoped>
.stock-pkg-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 132px;
  padding: 2px 0;
  text-align: left;
}

.stock-pkg-cell__row {
  display: grid;
  grid-template-columns: minmax(64px, 1fr) auto;
  align-items: center;
  gap: 8px 12px;
  padding: 5px 8px;
  background: #f3faf8;
  border: 1px solid #d7ebe7;
  border-radius: 8px;
  line-height: 1.2;
}

.stock-pkg-cell--with-cost .stock-pkg-cell__row {
  grid-template-columns: minmax(64px, 1fr) auto auto;
}

.stock-pkg-cell__row > span {
  min-width: 0;
  display: block;
}

.stock-pkg-cell__name {
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #1d7f72;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stock-pkg-cell__kg {
  display: block;
  margin-top: 1px;
  font-size: 10px;
  font-weight: 400;
  letter-spacing: 0;
  text-transform: none;
  color: #78948f;
}

.stock-pkg-cell__qty {
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #1b2a28;
  white-space: nowrap;
}

.stock-pkg-cell__cost {
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: #455a64;
  white-space: nowrap;
}

.stock-pkg-cell__cost--empty {
  color: #9e9e9e;
}
</style>
